<template>
  <div>
    <h2 class="font-header text-[3rem] font-bold text-primary mb-6">Newsletter Management</h2>

    <!-- Live Preview Section -->
    <div class="bg-white p-6 rounded-lg shadow-md mb-6">
      <h3 class="font-header text-[1.5rem] font-bold mb-4">Live Preview</h3>
      <div class="border border-gray-200 rounded-lg bg-gray-50 p-4">
        <iframe :srcdoc="emailPreviewHtml" class="w-full bg-white rounded shadow-sm"
          style="min-height: 600px; border: none;" title="Newsletter Preview"></iframe>
      </div>
    </div>

    <!-- Editor Section -->
    <div class="bg-white p-6 rounded-lg shadow-md mb-6">
      <h3 class="font-header text-[1.5rem] font-bold mb-4">Compose Newsletter</h3>
      <form @submit.prevent="sendNewsletter" class="space-y-4">
        <div>
          <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Language</label>
          <select v-model="newsletterData.language" required @change="loadSubscribers(1)"
            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]">
            <option value="english">English</option>
            <option value="french">French</option>
          </select>
        </div>
        <div>
          <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Subject</label>
          <input v-model="newsletterData.subject" type="text" required
            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
        </div>
        <div>
          <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Content</label>
          <div class="border border-gray-300 rounded-md">
            <RichTextEditor ref="editor" :content="newsletterData.content"
              @update:content="newsletterData.content = $event" placeholder="Write your newsletter content here..."
              :handlers="newsletterHandlers" />
          </div>
        </div>
        <div>
          <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Attachments</label>
          <div class="flex flex-col gap-2">
            <button type="button" @click="fileHandler"
              class="px-4 py-2 bg-gray-100 text-black rounded-md hover:bg-gray-200 transition-colors font-body text-[1.125rem] leading-[1.75]">
              Add Document Attachment
            </button>
            <p class="font-body text-[1.125rem] leading-[1.75] text-gray-500">Supported formats: PDF, DOC, DOCX, TXT,
              RTF (Max 500MB)</p>
            <div v-if="attachments.length > 0" class="space-y-2 mt-2">
              <div v-for="(attachment, index) in attachments" :key="index"
                class="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                <div class="flex items-center gap-2 flex-1 min-w-0">
                  <svg class="w-5 h-5 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z">
                    </path>
                  </svg>
                  <span class="font-body text-[1.125rem] leading-[1.75] truncate">{{ attachment.filename }}</span>
                </div>
                <button type="button" @click="removeAttachment(index)"
                  class="text-red-600 hover:text-red-800 text-[1.125rem] font-body leading-[1.75] ml-2 flex-shrink-0">Remove</button>
              </div>
            </div>
          </div>
        </div>
        <button type="submit" :disabled="sending"
          class="w-full px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-[1.125rem] leading-[1.75] disabled:opacity-50 disabled:cursor-not-allowed">
          {{ sending ? 'Sending...' : `Send Newsletter (${activeCount} recipients)` }}
        </button>
      </form>
    </div>

    <div class="bg-white rounded-lg shadow-md overflow-hidden">
      <div class="p-6 border-b border-creme-200">
        <h3 class="font-header text-[1.5rem] font-bold">Recipients ({{ activeCount }})</h3>
        <p class="font-body text-[1.125rem] leading-[1.75] text-gray-600 mt-1">Subscribers and administrators who will
          receive the newsletter</p>
      </div>
      <div class="max-h-96 overflow-y-auto">
        <div class="w-full overflow-x-auto">
          <table class="w-full min-w-max">
            <thead class="bg-green-700 text-white sticky top-0">
              <tr>
                <th class="px-4 py-3 text-left font-header">Email</th>
                <th class="px-4 py-3 text-left font-header">Language</th>
                <th class="px-4 py-3 text-left font-header">Type</th>
                <th class="px-4 py-3 text-left font-header">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="subscriber in subscribers" :key="subscriber.id"
                :class="['border-b border-creme-200 hover:bg-creme-50', subscriber.is_admin ? 'bg-gray-50' : '']">
                <td
                  :class="['px-4 py-3 font-body text-[1.125rem] leading-[1.75]', subscriber.is_admin ? 'text-gray-500' : 'text-gray-900']">
                  {{ subscriber.email }}
                </td>
                <td
                  :class="['px-4 py-3 font-body text-[1.125rem] leading-[1.75] capitalize', subscriber.is_admin ? 'text-gray-500' : 'text-gray-900']">
                  {{ subscriber.language || 'english' }}
                </td>
                <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75]">
                  <span v-if="subscriber.is_admin"
                    class="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-header">
                    Admin
                  </span>
                  <span v-else class="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-header">
                    Subscriber
                  </span>
                </td>
                <td class="px-4 py-3 font-body text-body">
                  <span :class="subscriber.is_active ? 'text-green-600' : 'text-red-600'">
                    {{ subscriber.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && subscribers.length === 0">
                <td colspan="4" class="px-4 py-8 text-center text-gray-500 font-body text-body">No recipients yet</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="loading" class="p-4 text-center">
          <Loader size="medium" />
        </div>
        <div v-if="pagination && pagination.total_pages > 1"
          class="p-4 flex justify-between items-center border-t border-creme-200">
          <button @click="loadSubscribers(currentPage - 1)" :disabled="!pagination.prev_page"
            :class="['px-4 py-2 rounded-md font-marketing', pagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
            Previous
          </button>
          <span class="font-marketing text-gray-700">
            Page {{ pagination.page }} of {{ pagination.total_pages }} ({{ pagination.total }} total)
          </span>
          <button @click="loadSubscribers(currentPage + 1)" :disabled="!pagination.next_page"
            :class="['px-4 py-2 rounded-md font-marketing', pagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { adminService } from '../../services/admin'
import Loader from '../Loader.vue'
import { useLoadingState } from '../../composables/useLoadingState'
import RichTextEditor from '../common/RichTextEditor.vue'
import { convertLegacyQuillContent } from '../../utils/quillConfig'

const subscribers = ref([])
const newsletterData = ref({
  subject: '',
  content: '',
  language: 'english'
})
const attachments = ref([])
const currentPage = ref(1)
const limit = ref(10)
const pagination = ref(null)
const activeCount = ref(0)
const { loading, setLoading } = useLoadingState(false)
const sending = ref(false)
const editor = ref(null)

// Computed property to generate email preview HTML using actual template
const emailPreviewHtml = computed(() => {
  const content = newsletterData.value.content || '<p style="color: #999; font-style: italic;">No content yet. Start typing to see your newsletter preview.</p>'
  const baseUrl = window.location.origin
  const year = new Date().getFullYear()

  // Use the correct logo URL
  const logoUrl = `${baseUrl}/logos/barry-bonna-email.png`

  // Build attachments HTML if any
  let attachmentsHtml = ''
  if (attachments.value.length > 0) {
    const attachmentsList = attachments.value.map(att =>
      `<li style="margin: 8px 0; color: #253439;">${att.filename}</li>`
    ).join('')
    attachmentsHtml = `
      <div style="margin: 30px 0; padding: 20px; background-color: #f9f9f9; border-radius: 4px;">
        <p style="color: #253439; font-size: 14px; font-weight: 600; margin: 0 0 12px 0;">📎 Attachments:</p>
        <ul style="margin: 0; padding-left: 20px; list-style-type: disc;">
          ${attachmentsList}
        </ul>
      </div>
    `
  }

  return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500;600;700&family=Manrope:wght@400;500;600;700&display=swap" rel="stylesheet">
    <title>Newsletter Preview</title>
    <style>
        body {
            margin: 0;
            padding: 0;
            font-family: 'EB Garamond', serif;
            background-color: #FFFFFF;
            color: #253439;
        }
        .company-logo-wrapper {
            pointer-events: none !important;
            user-select: none !important;
        }
        .newsletter-content {
          font-family: 'Manrope', sans-serif;
            font-size: 16px;
            line-height: 1.6;
            color: #253439;
        }
        /* Quill Editor Styles */
        .newsletter-content p {
            margin: 16px 0;
            color: #253439;
        }
        .newsletter-content h1,
        .newsletter-content .ql-size-huge {
            font-family: 'EB Garamond', serif;
            color: #253439;
            margin: 36px 0 24px 0;
            font-weight: normal;
            font-size: 28px;
        }
        .newsletter-content h2,
        .newsletter-content .ql-size-large {
            font-family: 'EB Garamond', serif;
            color: #253439;
            margin: 30px 0 20px 0;
            font-weight: normal;
            font-size: 24px;
        }
        .newsletter-content h3 {
            font-family: 'EB Garamond', serif;
            color: #253439;
            margin: 24px 0 16px 0;
            font-weight: normal;
            font-size: 20px;
        }
        .newsletter-content strong,
        .newsletter-content b {
            font-weight: bold;
            color: #253439;
        }
        .newsletter-content em,
        .newsletter-content i {
            font-style: italic;
        }
        .newsletter-content u {
            text-decoration: underline;
        }
        .newsletter-content s,
        .newsletter-content strike {
            text-decoration: line-through;
        }
        .newsletter-content a {
            color: #2D4D31;
            text-decoration: underline;
        }
        .newsletter-content ul,
        .newsletter-content ol {
            margin: 24px 0;
            padding-left: 45px;
        }
        .newsletter-content li {
            margin: 12px 0;
            color: #253439;
        }
        .newsletter-content blockquote {
            border-left: 4px solid #2D4D31;
            padding-left: 30px;
            margin: 30px 0;
            font-style: italic;
            color: #5B4F47;
        }
        .newsletter-content img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 30px auto;
            border: none;
        }
        .newsletter-content pre,
        .newsletter-content code {
            background-color: #f5f5f5;
            border: 1px solid #ddd;
            border-radius: 4px;
            padding: 2px 6px;
            font-family: 'Courier New', monospace;
            font-size: 14px;
        }
        .newsletter-content pre {
            padding: 16px;
            overflow-x: auto;
        }
        /* Quill text alignment */
        .newsletter-content .ql-align-left {
            text-align: left;
        }
        .newsletter-content .ql-align-center {
            text-align: center;
        }
        .newsletter-content .ql-align-right {
            text-align: right;
        }
        .newsletter-content .ql-align-justify {
            text-align: justify;
        }
        /* Quill text colors */
        .newsletter-content .ql-color-red {
            color: #e60000;
        }
        .newsletter-content .ql-color-orange {
            color: #f90;
        }
        .newsletter-content .ql-color-yellow {
            color: #ff0;
        }
        .newsletter-content .ql-color-green {
            color: #008a00;
        }
        .newsletter-content .ql-color-blue {
            color: #06c;
        }
        .newsletter-content .ql-color-purple {
            color: #93f;
        }
        /* Quill background colors */
        .newsletter-content .ql-bg-red {
            background-color: #e60000;
        }
        .newsletter-content .ql-bg-orange {
            background-color: #f90;
        }
        .newsletter-content .ql-bg-yellow {
            background-color: #ff0;
        }
        .newsletter-content .ql-bg-green {
            background-color: #008a00;
        }
        .newsletter-content .ql-bg-blue {
            background-color: #06c;
        }
        .newsletter-content .ql-bg-purple {
            background-color: #93f;
        }
        /* Quill fonts */
        .newsletter-content .ql-font-serif {
            font-family: Georgia, Times, 'Times New Roman', serif;
        }
        .newsletter-content .ql-font-manrope {
          font-family: 'Manrope', sans-serif;
        }
        .newsletter-content .ql-font-lora {
          font-family: 'Lora', serif;
        }
        .newsletter-content .ql-font-eb-garamond {
          font-family: 'EB Garamond', serif;
        }
        .newsletter-content .ql-font-georgia {
          font-family: 'Georgia', serif;
        }
        .newsletter-content .ql-font-times-new-roman {
          font-family: 'Times New Roman', serif;
        }
        .newsletter-content .ql-font-monospace {
            font-family: 'Courier New', monospace;
        }
    </style>
</head>
<body style="margin: 0; padding: 0; font-family: 'EB Garamond', serif; background-color: #FFFFFF; color: #253439;">
    <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #FFFFFF;">
        <tr>
            <td style="background-color: #2D4D31; padding: 12px 30px; text-align: center;">
                <img alt="Barry & Bonna Publishing" height="90" src="${logoUrl}" style="display:block;outline:none;border:0;text-decoration:none;width:auto;max-width:450px;height:90px;margin:0 auto;pointer-events:none;user-select:none;" draggable="false">
            </td>
        </tr>
        <tr>
            <td align="center" style="padding: 40px 20px;">
                <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 95%; background-color: #FFFFFF;">
                    <tr>
                        <td style="padding: 40px 30px;">
                            <div class="newsletter-content">
                                ${content}
                                ${attachmentsHtml}
                            </div>
                            <hr style="border: none; border-top: 1px solid #E8E8E8; margin: 45px 0;" />
                            <p style="color: #7C898B; font-size: 16px; line-height: 1.6; margin: 0;">
                                <a href="#" style="color: #7C898B; text-decoration: underline;">Unsubscribe</a> | 
                                <a href="${baseUrl}" style="color: #7C898B; text-decoration: underline;">Visit Website</a>
                            </p>
                        </td>
                    </tr>
                    <tr>
                        <td style="background-color: #FFFFFF; padding: 30px 30px; text-align: center; border-top: 1px solid #E8E8E8;">
                            <p style="color: #7C898B; font-size: 14px; margin: 0;">
                                © ${year} <i>Barry & Bonna Publishing</i> LLC. All rights reserved.
                            </p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>
  `
})

const newsletterHandlers = {
  image: imageHandler,
  video: videoHandler,
  link: linkHandler,
  attachment: linkHandler
}

async function imageHandler() {
  const input = document.createElement('input')
  input.setAttribute('type', 'file')
  input.setAttribute('accept', 'image/*,.heic,.heif')
  input.click()

  input.onchange = async () => {
    const file = input.files[0]
    if (!file) return

    const isImage = file.type.startsWith('image/') ||
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif')
    if (!isImage) {
      alert('Please select an image file')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB')
      return
    }

    try {
      const { convertHeicForUpload } = await import('../../utils/heicConverter')
      const fileToUpload = await convertHeicForUpload(file)

      const formData = new FormData()
      formData.append('file', fileToUpload)

      const url = await adminService.uploadImage(formData)
      if (url && editor.value) {
        const quill = editor.value.getQuill()
        const range = quill.getSelection(true)
        if (range) {
          quill.insertEmbed(range.index, 'image', url)
        }
      }
    } catch (error) {
      console.error('Failed to upload image:', error)
      alert('Failed to upload image. Please try again.')
    }
  }
}

async function videoHandler() {
  const input = document.createElement('input')
  input.setAttribute('type', 'file')
  input.setAttribute('accept', 'video/*')
  input.click()

  input.onchange = async () => {
    const file = input.files[0]
    if (!file) return

    if (!file.type.startsWith('video/')) {
      const url = prompt('Please select a video file or enter a video URL (YouTube, Vimeo, etc.):')
      if (url && editor.value) {
        const quill = editor.value.getQuill()
        const range = quill.getSelection(true)

        if (!range) {
          const length = quill.getLength()
          quill.setSelection(length - 1)
        }

        const currentRange = quill.getSelection(true)

        if (url.includes('youtube.com') || url.includes('youtu.be')) {
          const videoId = extractYouTubeId(url)
          if (videoId) {
            const embedUrl = `https://www.youtube.com/embed/${videoId}`
            quill.insertEmbed(currentRange.index, 'video', embedUrl)
          } else {
            alert('Invalid YouTube URL')
          }
        } else if (url.includes('vimeo.com')) {
          const videoId = extractVimeoId(url)
          if (videoId) {
            const embedUrl = `https://player.vimeo.com/video/${videoId}`
            quill.insertEmbed(currentRange.index, 'video', embedUrl)
          } else {
            alert('Invalid Vimeo URL')
          }
        } else {
          quill.insertEmbed(currentRange.index, 'video', url)
        }
      }
      return
    }

    if (file.size > 200 * 1024 * 1024) {
      alert('Video size must be less than 200MB')
      return
    }

    try {
      const formData = new FormData()
      formData.append('file', file)

      const url = await adminService.uploadNewsletterVideo(formData)
      if (url && editor.value) {
        const quill = editor.value.getQuill()
        const range = quill.getSelection(true)
        if (range) {
          const videoHtml = `<div style="margin: 20px 0; text-align: center;">
            <video controls width="100%" style="max-width: 100%; border-radius: 4px;">
              <source src="${url}" type="video/mp4">
            </video>
            <p style="margin-top: 10px; font-size: 14px; color: #7C898B;">
              <a href="${url}" target="_blank" style="color: #2D4D31; text-decoration: underline;">Watch in new tab</a>
            </p>
          </div>`
          quill.clipboard.dangerouslyPasteHTML(range.index, videoHtml)
        }
      }
    } catch (error) {
      console.error('Failed to upload video:', error)
      alert('Failed to upload video. Please try again.')
    }
  }
}

function extractYouTubeId(url) {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  return (match && match[2].length === 11) ? match[2] : null
}

function extractVimeoId(url) {
  const regExp = /vimeo.com\/(\d+)/
  const match = url.match(regExp)
  return match ? match[1] : null
}

async function linkHandler(value) {
  if (!editor.value) return

  const quill = editor.value.getQuill()
  const range = quill.getSelection(true)
  if (!range) return

  if (value) {
    const href = prompt('Enter the URL:', value)
    if (href) {
      if (range.length > 0) {
        quill.formatText(range.index, range.length, 'link', href)
      } else {
        quill.insertText(range.index, href, 'link', href)
      }
    }
    return
  }

  const input = document.createElement('input')
  input.setAttribute('type', 'file')
  input.setAttribute('accept', '.pdf,.doc,.docx,.txt,.rtf,.odt,.pages')
  input.click()

  input.onchange = async () => {
    const file = input.files[0]

    if (!file) {
      const href = prompt('Enter the URL:')
      if (href) {
        const currentRange = quill.getSelection(true)
        if (currentRange) {
          if (currentRange.length > 0) {
            quill.formatText(currentRange.index, currentRange.length, 'link', href)
          } else {
            quill.insertText(currentRange.index, href, 'link', href)
          }
        }
      }
      return
    }

    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.odt', '.pages']
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()

    if (!allowedExtensions.includes(fileExtension)) {
      alert('Please select a document file (PDF, DOC, DOCX, TXT, RTF, ODT, or PAGES)')
      const href = prompt('Enter the URL:')
      if (href) {
        const currentRange = quill.getSelection(true)
        if (currentRange) {
          if (currentRange.length > 0) {
            quill.formatText(currentRange.index, currentRange.length, 'link', href)
          } else {
            quill.insertText(currentRange.index, href, 'link', href)
          }
        }
      }
      return
    }

    if (file.size > 500 * 1024 * 1024) {
      alert('File size must be less than 500MB')
      const href = prompt('Enter the URL:')
      if (href) {
        const currentRange = quill.getSelection(true)
        if (currentRange) {
          if (currentRange.length > 0) {
            quill.formatText(currentRange.index, currentRange.length, 'link', href)
          } else {
            quill.insertText(currentRange.index, href, 'link', href)
          }
        }
      }
      return
    }

    try {
      const formData = new FormData()
      formData.append('file', file)

      const result = await adminService.uploadNewsletterFile(formData)
      if (result && result.url) {
        const currentRange = quill.getSelection(true)
        if (currentRange) {
          const linkText = currentRange.length > 0 ? quill.getText(currentRange.index, currentRange.length) : file.name
          if (currentRange.length > 0) {
            quill.formatText(currentRange.index, currentRange.length, 'link', result.url)
          } else {
            quill.insertText(currentRange.index, linkText, 'link', result.url)
          }
        }
      }
    } catch (error) {
      console.error('Failed to upload file:', error)
      alert('Failed to upload file. Please try again.')
      const href = prompt('Enter the URL:')
      if (href) {
        const currentRange = quill.getSelection(true)
        if (currentRange) {
          if (currentRange.length > 0) {
            quill.formatText(currentRange.index, currentRange.length, 'link', href)
          } else {
            quill.insertText(currentRange.index, href, 'link', href)
          }
        }
      }
    }
  }
}

const loadSubscribers = async (page = 1) => {
  setLoading(true)
  try {
    const response = await adminService.getNewsletterSubscribers(page, limit.value, newsletterData.value.language)
    if (response.data && response.pagination) {
      subscribers.value = response.data
      pagination.value = response.pagination
      activeCount.value = response.active_count || 0
      currentPage.value = page
    } else {
      subscribers.value = Array.isArray(response) ? response : []
      pagination.value = null
      activeCount.value = 0
    }
  } catch (error) {
    console.error('Failed to load subscribers:', error)
    subscribers.value = []
    pagination.value = null
    activeCount.value = 0
  } finally {
    setLoading(false)
  }
}

async function fileHandler() {
  const input = document.createElement('input')
  input.setAttribute('type', 'file')
  input.setAttribute('accept', '.pdf,.doc,.docx,.txt,.rtf,.odt,.pages')
  input.click()

  input.onchange = async () => {
    const file = input.files[0]
    if (!file) return

    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.odt', '.pages']
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()

    if (!allowedExtensions.includes(fileExtension)) {
      alert('Please select a document file (PDF, DOC, DOCX, TXT, RTF, ODT, or PAGES)')
      return
    }

    if (file.size > 500 * 1024 * 1024) {
      alert('File size must be less than 500MB')
      return
    }

    try {
      const formData = new FormData()
      formData.append('file', file)

      const result = await adminService.uploadNewsletterFile(formData)
      if (result && result.url) {
        attachments.value.push({
          url: result.url,
          filename: result.filename || file.name
        })
      }
    } catch (error) {
      console.error('Failed to upload file:', error)
      alert('Failed to upload file. Please try again.')
    }
  }
}

function removeAttachment(index) {
  attachments.value.splice(index, 1)
}

const sendNewsletter = async () => {
  if (!newsletterData.value.subject || !newsletterData.value.content) {
    alert('Please fill in both subject and content')
    return
  }

  sending.value = true
  try {
    const payload = {
      ...newsletterData.value,
      attachments: attachments.value
    }
    const result = await adminService.sendNewsletter(payload)
    let message = `Newsletter sent successfully! Success: ${result.success_count}, Failures: ${result.failure_count}`
    if (result.failed_emails && result.failed_emails.length > 0) {
      message += `\nFailed to send to the following emails:\n` + result.failed_emails.join('\n')
    }
    alert(message)
    newsletterData.value = {
      subject: '',
      content: '',
      language: 'english'
    }
    attachments.value = []
    if (editor.value) {
      editor.value.setHTML(convertLegacyQuillContent(''))
    }
    await loadSubscribers(currentPage.value)
  } catch (error) {
    console.error('Failed to send newsletter:', error)
    alert('Failed to send newsletter. Please try again.')
  } finally {
    sending.value = false
  }
}

onMounted(() => {
  loadSubscribers(1)
})
</script>

<style scoped>
:deep(.ql-editor) {
  min-height: 300px;
  line-height: 1.75;
  color: #253439;
  direction: ltr !important;
  text-align: left !important;
  unicode-bidi: embed !important;
}

:deep(.ql-container) {
  font-family: 'Manrope', sans-serif;
  direction: ltr !important;
  unicode-bidi: embed !important;
  border-bottom-left-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}

:deep(.ql-toolbar) {
  border-top-left-radius: 0.375rem;
  border-top-right-radius: 0.375rem;
  background-color: #FAFAFA;
}

:deep(.ql-editor *) {
  direction: ltr !important;
}
</style>
